import { TypedDocumentNode, useBackgroundQuery, OperationVariables } from "@apollo/client";
import { QueryRef } from "@apollo/client/react/types/types";
import { useDeferredValue } from "react";

export interface DataGridQueryOptions<TData, TVariables extends OperationVariables> {
  query: TypedDocumentNode<TData, TVariables>;
  variables: TVariables;
  /**
   * Skip the query entirely (useful for conditional fetching)
   */
  skip?: boolean;
  /**
   * Apollo fetch policy for background queries. Defaults to "cache-and-network".
   * Note: "standby" is not supported by useBackgroundQuery.
   */
  fetchPolicy?: "cache-first" | "cache-and-network" | "network-only" | "no-cache";
}

export interface DataGridQueryResult<TData, TVariables extends OperationVariables> {
  /**
   * Query ref to pass to SuspenseDataGrid
   */
  queryRef: QueryRef<TData, TVariables> | null;
  /**
   * Whether variables are being deferred (for opacity transition)
   */
  isPending: boolean;
}

/**
 * Hook for DataGrid queries using Apollo's Suspense pattern.
 *
 * Uses `useDeferredValue` to keep showing stale data while new data loads.
 * The `isPending` flag indicates when deferred variables differ from current.
 *
 * Pagination, sort, and refetch are handled by callers building their own
 * variables and re-invoking this hook — there used to be convenience wrappers
 * (`setPaginationModel`, `setSortModel`, `refetch`) that called
 * `apolloRefetch({ ...variables, page, pageSize } as unknown as Partial<TVariables>)`,
 * which silently no-op'd whenever a caller's query used different field names.
 * Since the only in-tree caller (`OmnisearchDataGridV2`) drives pagination/sort
 * through its own `buildVariables` + memoized `variables` flow, the wrappers
 * were unused dead code that hid type holes. Removed.
 */
export function useDataGridQuery<TData, TVariables extends OperationVariables>(
  options: DataGridQueryOptions<TData, TVariables>
): DataGridQueryResult<TData, TVariables> {
  const { query, variables, skip = false, fetchPolicy = "cache-and-network" } = options;

  // Defer variables so React shows stale data during transitions
  const deferredVariables = useDeferredValue(variables);
  const isPending = deferredVariables !== variables;

  const [queryRef] = useBackgroundQuery(query, {
    variables: deferredVariables,
    fetchPolicy,
    skip,
  });

  return {
    queryRef: skip ? null : (queryRef ?? null),
    isPending,
  };
}
